//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { postRequest } from "../../../utils/jha-fetch";
import { Apis } from "../../../utils/api-constants";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util";
import { GlobalVariables } from "../../../utils/constants";
import { routerConfig } from "../../../routers";
import { removeAllCookies } from "../../../utils/removeAllCookies";
import { decryptValue } from "../../../utils/app-utils";
import { StatusConstants } from "../../../utils/apiStatusConstants";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import {
  getCredentialHistoryPersistWarning,
  isCredentialUpdateSuccess,
} from "../../../utils/credential-history-warning";
import Router from "@jack-henry/web-component-router";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import Cookies from "js-cookie";
import { openForcedLogoutNotice } from "../../../Components/JhftJhaDialog";
import "@banno/jha-wc/src/icons/jha-icon-show";

@customElement("change-password-view")
export class ChangePasswordView extends formElementMixin(LitElement) {
  constructor() {
    super();
  }

  // Password validation states
  @state() private atLeastMinLength = false;
  @state() private atLeastOneLetter = false;
  @state() private atLeastOneNumber = false;
  @state() private atLeastOneUpperCase = false;
  @state() private atLeastOneLowerCase = false;
  @state() private atLeastOneSpecial = false;
  @state() private cannotBeginOrEndInSpaces = false;
  @state() private newPassAndConfirmPassAreSame = false;

  @property({ type: Object })
  formData = {
    userId: decryptValue(Cookies.get("userId") || "") ?? "",
    newPassword: "",
    confirmPassword: "",
    currentPassword: "",
  };

  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) showCurrentPassword = false;
  @property({ type: Boolean }) showNewPassword = false;
  @property({ type: Boolean }) showConfirmPassword = false;

  @property({ type: Number }) passwordMinLength = GlobalVariables.USER_PASSWORD_MIN_LENGTH;

  @property({
    type: Boolean,
    hasChanged(newVal: boolean, oldVal: boolean) {
      return newVal !== oldVal;
    },
  })
  oneLetterOneNum = false;

  static readonly styles = css`
    #chgPassCon {
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
      padding: 24px;
    }
    p > [icon] {
      margin-right: 8px;
      vertical-align: middle;
    }
    jha-icon-circle-checkmark-filled {
      fill: var(--jha-color-success);
    }
    @media (prefers-color-scheme: dark) {
      h1 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h1 {
        color: #06185f;
      }
    }
    form {
      display: flex;
      flex-direction: column;
      gap: 30px;
      flex: 1;
    }
    jha-form-row {
      display: flex;
      justify-content: flex-start;
      gap: 40px;
    }
    jha-form-input {
      max-width: 248px;
    }
    jha-card {
      padding: 24px;
      width: -webkit-fill-available;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    jha-button {
      max-width: 147px;
    }
    .password-requirements {
      margin-bottom: 0.5em;
      margin-top: 0.5em;
    }
    .password-container {
      position: relative;
    }
    .current-password-container {
      position: relative;
      display: inline-block;
      width: 100%;
    }
    .current-password-container jha-form-input {
      width: 100%;
      position: relative;
    }
    .current-password-container jha-form-input::part(input) {
      padding-right: 40px;
    }
    .current-password-container jha-form-input::part(input-container) {
      position: relative;
    }
    .password-toggle {
      position: absolute;
      right: 12px;
      top: 34px;
      cursor: pointer;
      z-index: 10;
      color: #666;
      padding: 4px;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .current-password-toggle {
      position: absolute;
      right: 8px;
      cursor: pointer;
      z-index: 1000;
      color: #666;
      padding: 4px;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      background-color: transparent;
      height: 20px;
      width: 20px;
      box-sizing: content-box;
    }
    .password-toggle:hover,
    .current-password-toggle:hover {
      color: #4157ad;
    }
    .password-toggle jha-icon-show,
    .current-password-toggle jha-icon-show {
      width: 20px;
      height: 20px;
    }
  `;

  private resetAllRequirementStates() {
    this.atLeastMinLength = false;
    this.atLeastOneLetter = false;
    this.atLeastOneNumber = false;
    this.atLeastOneUpperCase = false;
    this.atLeastOneLowerCase = false;
    this.atLeastOneSpecial = false;
    this.cannotBeginOrEndInSpaces = false;
    this.newPassAndConfirmPassAreSame = false;
  }

  private get allPasswordValidationsPass(): boolean {
    return (
      this.atLeastMinLength &&
      this.atLeastOneLetter &&
      this.atLeastOneNumber &&
      this.atLeastOneUpperCase &&
      this.atLeastOneLowerCase &&
      this.atLeastOneSpecial &&
      this.cannotBeginOrEndInSpaces &&
      this.newPassAndConfirmPassAreSame &&
      this.formData.currentPassword.trim() !== ""
    );
  }

  async onChangePasswordClick() {
    if (this.allPasswordValidationsPass) {
      this.apiLoading = true;
      const req = {
        userId: this.formData.userId,
        currentPassword: this.formData.currentPassword,
        newPassword: this.formData.confirmPassword,
      };
      const changePassword = await postRequest<{ userId: string; newPassword: string; currentPassword: string }>(
        Apis.CHANGE_PASSWORD,
        req
      );
      if (isCredentialUpdateSuccess(changePassword)) {
        this.apiLoading = false;
        showToast({
          message: "Password updated successfully.",
          role: "success",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
        const historyWarning = getCredentialHistoryPersistWarning(changePassword);
        if (historyWarning) {
          showToast({
            message: historyWarning,
            role: "warning",
            duration: GlobalVariables.TOAST_TIMEOUT,
          });
        }
        setTimeout(() => {
          removeAllCookies();
          const router = new Router(routerConfig);
          router.go("/");
        }, 2000);
      } else if (changePassword?.details?.length > 0) {
        if (changePassword.details[0].code === StatusConstants.ACC_LOCK_CODE) {
          this.apiLoading = false;
          await openForcedLogoutNotice("accountLocked");
          clearInterval(this.timerId);
          removeAllCookies();
          window.location.href = "/login";
        } else {
          this.apiLoading = false;
          showToast({
            message: getErrorMessage(changePassword.details),
            role: "danger",
            duration: GlobalVariables.TOAST_TIMEOUT,
          });
        }
      } else if (changePassword?.status === StatusConstants.UNAUTHORIZED_USER_CODE) {
        this.apiLoading = false;
        showToast({
          message: "Incorrect current Password.",
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      } else {
        this.apiLoading = false;
        showToast({
          message: "Something went wrong, please try again.",
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    } else {
      showToast({
        message: "New Password Must Contain All Items Listed Below.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }

  onNewPasswordChange(e: { target: { value: string } }) {
    const newPass = e.target.value ?? "";
    this.resetAllRequirementStates();

    this.atLeastMinLength = newPass.length >= this.passwordMinLength;
    this.atLeastOneLetter = /[a-zA-Z]/.test(newPass);
    this.atLeastOneNumber = /[0-9]/.test(newPass);
    this.atLeastOneUpperCase = /[A-Z]/.test(newPass);
    this.atLeastOneLowerCase = /[a-z]/.test(newPass);
    this.atLeastOneSpecial = /[^a-zA-Z0-9]/.test(newPass);
    this.cannotBeginOrEndInSpaces = newPass.length > 0 && !/^\s|\s$/.test(newPass);
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(newPass, this.formData.confirmPassword);
    this.formData = { ...this.formData, newPassword: newPass };
  }

  onConfirmPasswordChange(e: { target: { value: string } }) {
    const confirmPass = e.target.value ?? "";
    this.newPassAndConfirmPassAreSame = this.passwordsMatch(this.formData.newPassword, confirmPass);

    this.updateProperty("confirmPassword", confirmPass);
  }

  private passwordsMatch(newPassword: string, confirmPassword: string): boolean {
    const trimmedNew = newPassword?.trim();
    const trimmedConfirm = confirmPassword?.trim();
    return trimmedNew?.length > 0 && trimmedConfirm?.length > 0 && trimmedNew === trimmedConfirm;
  }

  onCurrentPasswordIconClick() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  onNewPasswordIconClick() {
    this.showNewPassword = !this.showNewPassword;
  }

  onConfirmPasswordIconClick() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  positionCurrentPasswordIcon() {
    const container = this.shadowRoot?.querySelector(".current-password-container") as HTMLElement;
    const toggle = this.shadowRoot?.querySelector(".current-password-toggle") as HTMLElement;
    const input = this.shadowRoot?.querySelector("jha-form-input[data-id='currentPassword']") as HTMLElement;

    if (container && toggle && input) {
      // Find the actual input element within the shadow DOM
      const inputShadowRoot = input.shadowRoot;
      const actualInput = inputShadowRoot?.querySelector("input") as HTMLInputElement;

      if (actualInput && container && toggle) {
        // Get positions relative to the viewport
        const containerRect = container.getBoundingClientRect();
        const inputRect = actualInput.getBoundingClientRect();
        const toggleRect = toggle.getBoundingClientRect();
        
        // Get actual computed heights (including padding and borders)
        const inputHeight = inputRect.height; // Use getBoundingClientRect height which includes padding
        const toggleHeight = toggle.offsetHeight || toggleRect.height; // Get actual height including padding
        
        // Calculate vertical centering: equal distance from top and bottom
        const inputTopRelativeToContainer = inputRect.top - containerRect.top;
        
        // Center the icon vertically: input top + (input height / 2) - (toggle height / 2)
        const inputCenterY = inputTopRelativeToContainer + (inputHeight / 2);
        const iconTop = inputCenterY - (toggleHeight / 2);
        
        // Calculate right position: distance from container's right edge to input's right edge, then add 8px
        const inputRightRelativeToContainer = inputRect.right - containerRect.left;
        const containerWidth = containerRect.width;
        const iconRight = containerWidth - inputRightRelativeToContainer + 8; // 8px from input's right edge
        
        toggle.style.top = `${iconTop}px`;
        toggle.style.right = `${iconRight}px`;
        toggle.style.position = "absolute";
      }
    }
  }

  firstUpdated() {
    super.firstUpdated();
    // Wait for the DOM to fully render, use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.positionCurrentPasswordIcon();
      }, 150);
    });
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    // Reposition icon when password visibility changes
    if (changedProperties.has("showCurrentPassword")) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.positionCurrentPasswordIcon();
        }, 100);
      });
    }
  }

  render() {
    return html`
      ${this.apiLoading ? html`<circle-loader></circle-loader>` : undefined}
      <div id="chgPassCon">
        <h1>Change Password</h1>
        <jha-card>
          <form>
            <jha-form-row>
              <div class="current-password-container">
                <jha-form-input
                  data-auto-id="currentPasswordInput"
                  label="Current Password"
                  required
                  warning="Current password required"
                  type=${this.showCurrentPassword ? "text" : "password"}
                  outline
                  data-id="currentPassword"
                  @change=${this.inputValueChange}
                maxlength="256">
                </jha-form-input>
                <div
                  class="current-password-toggle"
                  @click=${this.onCurrentPasswordIconClick}
                  title="Click to show/hide password"
                >
                  <jha-icon-show></jha-icon-show>
                </div>
              </div>
            </jha-form-row>
            <jha-form-row>
              <div class="password-container">
                <jha-form-input
                  data-auto-id="newPasswordInput"
                  label="New Password"
                  required
                  warning="New password Required"
                  type=${this.showNewPassword ? "text" : "password"}
                  outline
                  data-id="newPassword"
                  @change=${this.onNewPasswordChange}
                maxlength="60">
                </jha-form-input>
                <div
                  class="password-toggle"
                  @click=${this.onNewPasswordIconClick}
                  title="Click to show/hide password"
                >
                  <jha-icon-show></jha-icon-show>
                </div>
              </div>
              <div class="password-container">
                <jha-form-input
                  data-auto-id="confirmPasswordInput"
                  label="Confirm New Password"
                  required
                  warning="Confirm New Password Required"
                  type=${this.showConfirmPassword ? "text" : "password"}
                  outline
                  data-id="confirmPassword"
                  @change=${this.onConfirmPasswordChange}
                maxlength="60">
                </jha-form-input>
                <div
                  class="password-toggle"
                  @click=${this.onConfirmPasswordIconClick}
                  title="Click to show/hide password"
                >
                  <jha-icon-show></jha-icon-show>
                </div>
              </div>
            </jha-form-row>
            <div id="disabled">
              <p id="passTitle">Password Requirements:</p>
              <p class="password-requirements">
                ${this.atLeastMinLength
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must be at least ${this.passwordMinLength} characters.
              </p>
              <p class="password-requirements">
                ${this.atLeastOneLetter
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must contain at least one letter.
              </p>
              <p class="password-requirements">
                ${this.atLeastOneNumber
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must contain at least one number.
              </p>
              <p class="password-requirements">
                ${this.atLeastOneUpperCase
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must contain at least one uppercase letter.
              </p>
              <p class="password-requirements">
                ${this.atLeastOneLowerCase
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must contain at least one lowercase letter.
              </p>
              <p class="password-requirements">
                ${this.atLeastOneSpecial
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Must contain at least one special character.
              </p>
              <p class="password-requirements">
                ${this.cannotBeginOrEndInSpaces
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                Cannot begin or end with a space.
              </p>
              <p class="password-requirements">
                ${this.newPassAndConfirmPassAreSame
                  ? html`<jha-icon-circle-checkmark-filled icon></jha-icon-circle-checkmark-filled>`
                  : html`<jha-icon-circle-checkmark icon></jha-icon-circle-checkmark>`}
                New Password must be the same as Confirm Password.
              </p>
            </div>
            <div style="flex:1;"></div>
            <jha-button
              data-auto-id="changePasswordBtn"
              large
              sync
              ?disabled=${!this.allPasswordValidationsPass}
              @click=${this.onChangePasswordClick}>
              Change Password
            </jha-button>
          </form>
        </jha-card>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "change-password-view": ChangePasswordView;
  }
}
